import { useMemo, useState } from "react";
import { useGoogleAuth } from "./auth/useGoogleAuth";
import { useDashboard } from "./data/useDashboard";
import { Connect } from "./components/Connect";
import { Header } from "./components/Header";
import { HomeView } from "./components/HomeView";
import { ProjectView } from "./components/ProjectView";
import { SetupPanel } from "./components/SetupPanel";
import { TaskDialog } from "./components/TaskDialog";
import { DoneGate } from "./components/DoneGate";
import { ProjectDialog } from "./components/ProjectDialog";
import { ArchiveDialog } from "./components/ArchiveDialog";
import { Button } from "./components/ui/Button";
import { todayIso } from "./lib/dates";
import { mindmapUrl } from "./lib/links";
import type { Project, Status, Task, TaskDraft } from "./types";

type Editing = { mode: "new" } | { mode: "edit"; task: Task } | null;
type Gate =
  | { kind: "new"; draft: TaskDraft }
  | { kind: "move"; task: Task }
  | null;

export default function App() {
  const auth = useGoogleAuth();
  const data = useDashboard({ token: auth.token, onUnauthorized: auth.invalidate });

  const [projectId, setProjectId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [gate, setGate] = useState<Gate>(null);
  const [newProject, setNewProject] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [archiving, setArchiving] = useState<Project | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const current = projectId ? data.projectById.get(projectId) ?? null : null;
  const visibleTasks = useMemo(
    () => (current ? data.tasks.filter((t) => t.projectId === current.id) : data.tasks),
    [current, data.tasks]
  );

  if (!auth.token) {
    return <Connect ready={auth.ready} error={auth.error} onConnect={auth.connect} />;
  }

  if (data.setup) {
    return (
      <div className="wrap">
        <SetupPanel plan={data.setup} busy={data.loading} onRun={data.applySetup} />
        {data.error && <p className="notice">{data.error}</p>}
      </div>
    );
  }

  const openMindmap = (project: Project) => {
    const url = mindmapUrl(project.mindmap);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const saveTask = (draft: TaskDraft, wantsDone: boolean) => {
    if (wantsDone) {
      setEditing(null);
      setGate({
        kind: editing?.mode === "edit" ? "move" : "new",
        ...(editing?.mode === "edit"
          ? { task: { ...editing.task, ...draft } }
          : { draft }),
      } as Gate);
      return;
    }
    const stamped: TaskDraft = {
      ...draft,
      dateDone: draft.status === "Done" ? draft.dateDone || todayIso() : "",
    };
    if (editing?.mode === "edit") {
      void data.updateTask({ ...editing.task, ...stamped });
    } else {
      void data.createTask(stamped);
    }
    setEditing(null);
  };

  const moveTask = (task: Task, status: Status) => {
    if (status === "Done" && !task.resultLink) {
      setGate({ kind: "move", task: { ...task, status } });
      return;
    }
    void data.updateTask({
      ...task,
      status,
      dateDone: status === "Done" ? task.dateDone || todayIso() : "",
      // Moving back out of Done keeps the link and clears the reason.
      outcome: status === "Done" ? task.outcome : "",
    });
  };

  const commitGate = (result: { resultLink: string; outcome: string }) => {
    if (!gate) return;
    if (gate.kind === "move") {
      void data.updateTask({
        ...gate.task,
        status: "Done",
        dateDone: gate.task.dateDone || todayIso(),
        ...result,
      });
    } else {
      void data.createTask({
        ...gate.draft,
        status: "Done",
        dateDone: todayIso(),
        ...result,
      });
    }
    setGate(null);
  };

  const takenColors = data.projects.map((p) => p.color);

  return (
    <div className="wrap">
      <Header
        projects={data.projects}
        currentProjectId={projectId}
        onPick={setProjectId}
        action={
          current ? (
            <Button
              variant="primary"
              disabled={current.archived}
              onClick={() => setEditing({ mode: "new" })}
            >
              New task
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setNewProject(true)}>
              New project
            </Button>
          )
        }
      />

      {data.error && (
        <p className="notice">
          {data.error}{" "}
          <button className="notice__close" onClick={data.dismissError}>
            Dismiss
          </button>
        </p>
      )}

      {data.calendarNote && (
        <p className="notice notice--soft">
          {data.calendarNote}{" "}
          <button className="notice__close" onClick={data.dismissCalendarNote}>
            Dismiss
          </button>
        </p>
      )}

      {current ? (
        <ProjectView
          project={current}
          tasks={visibleTasks}
          onOpenTask={(task) => setEditing({ mode: "edit", task })}
          onMove={moveTask}
          onArchive={() => setArchiving(current)}
          onRestore={() => void data.updateProject({ ...current, archived: false })}
          onEdit={() => setEditingProject(current)}
          onMindmap={() => openMindmap(current)}
          onHome={() => setProjectId(null)}
        />
      ) : (
        <HomeView
          projects={data.projects}
          tasks={data.tasks}
          projectById={data.projectById}
          onEnter={setProjectId}
          onOpenTask={(task) => {
            setProjectId(task.projectId);
            const project = data.projectById.get(task.projectId);
            if (!project?.archived) setEditing({ mode: "edit", task });
          }}
          onArchive={setArchiving}
          onRestore={(p) => void data.updateProject({ ...p, archived: false })}
          onEdit={setEditingProject}
          onMindmap={openMindmap}
          showArchived={showArchived}
          onToggleArchived={() => setShowArchived((v) => !v)}
        />
      )}

      {editing && current && (
        <TaskDialog
          task={editing.mode === "edit" ? editing.task : null}
          project={current}
          onSave={saveTask}
          onDelete={
            editing.mode === "edit"
              ? () => {
                  void data.deleteTask(editing.task);
                  setEditing(null);
                }
              : null
          }
          onClose={() => setEditing(null)}
        />
      )}

      {gate && (
        <DoneGate
          title={gate.kind === "move" ? gate.task.title : gate.draft.title}
          resultLink={gate.kind === "move" ? gate.task.resultLink : gate.draft.resultLink}
          onCommit={commitGate}
          onCancel={() => setGate(null)}
        />
      )}

      {editingProject && (
        <ProjectDialog
          project={editingProject}
          takenColors={takenColors.filter((c) => c !== editingProject.color)}
          onSave={(draft) => {
            // A rename has to rewrite column B on every task row, which is what
            // keeps the spreadsheet readable to a human opening it directly.
            const renamed = draft.name !== editingProject.name;
            void data.updateProject({ ...editingProject, ...draft }, renamed);
            setEditingProject(null);
          }}
          onClose={() => setEditingProject(null)}
        />
      )}

      {newProject && (
        <ProjectDialog
          project={null}
          takenColors={takenColors}
          onSave={async (draft) => {
            const saved = await data.createProject(draft);
            setNewProject(false);
            if (saved) setProjectId(saved.id);
          }}
          onClose={() => setNewProject(false)}
        />
      )}

      {archiving && (
        <ArchiveDialog
          project={archiving}
          openTasks={data.tasks.filter(
            (t) => t.projectId === archiving.id && t.status !== "Done"
          )}
          onConfirm={() => {
            void data.updateProject({ ...archiving, archived: true });
            if (projectId === archiving.id) setProjectId(null);
            setShowArchived(true);
            setArchiving(null);
          }}
          onClose={() => setArchiving(null)}
        />
      )}

      {data.toast && <div className="toast">{data.toast}</div>}

      <div className="foot">
        <span>
          {data.loading ? "Loading…" : `${data.tasks.length} tasks in the sheet`}
        </span>
        <Button variant="quiet" onClick={() => void data.refresh()}>
          Refresh
        </Button>
        <Button variant="quiet" onClick={auth.disconnect}>
          Disconnect
        </Button>
      </div>
    </div>
  );
}
